import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDay } from "@/lib/utils";
import { RichTextEditor, AutoText } from "@/components/RichText";
import { CloseButton } from "@/components/CloseButton";

const RATINGS = ["exceeds", "meets", "developing", "needs_improvement"] as const;
type Rating = typeof RATINGS[number];

interface Review {
  id: string; employee_id: string; reviewer_id: string; cycle_name: string | null;
  period_start: string; period_end: string;
  overall_rating: Rating | null;
  strengths: string | null; growth_areas: string | null; goals_for_next_period: string | null;
  summary: string | null;
  status: "draft" | "sent_to_employee" | "employee_signed" | "closed";
  sent_at: string | null; employee_signed_at: string | null;
  employee_comments: string | null; closed_at: string | null;
  created_at: string;
}
interface Competency { id: string; review_id: string; name: string; rating: Rating | null; comments: string | null; sort_order: number; }
interface Person { id: string; first_name: string | null; last_name: string | null; email: string; }

export function ReviewsAdmin() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Review[]>([]);
  const [emps, setEmps] = useState<Person[]>([]);
  const [editing, setEditing] = useState<Review | null>(null);
  const [newFor, setNewFor] = useState("");
  const [filter, setFilter] = useState<"all" | Review["status"]>("all");

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: e }] = await Promise.all([
      supabase.from("hq_performance_reviews").select("*").order("created_at", { ascending: false }),
      supabase.from("hq_employees_people").select("id,first_name,last_name,email").eq("status", "active").order("first_name"),
    ]);
    setRows((r as Review[]) ?? []); setEmps((e as Person[]) ?? []);
  }
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);

  async function create() {
    if (!newFor || !employee) return;
    const today = new Date();
    const periodEnd = today.toISOString().slice(0, 10);
    const periodStart = new Date(today.getTime() - 90 * 86400000).toISOString().slice(0, 10);
    const { data, error } = await supabase.from("hq_performance_reviews").insert({
      employee_id: newFor, reviewer_id: employee.id,
      period_start: periodStart, period_end: periodEnd,
      cycle_name: "Q" + Math.ceil((today.getMonth() + 1) / 3) + " " + today.getFullYear(),
    }).select().single();
    if (!error && data) {
      // Seed default competencies
      const defaults = [
        "Quality of work", "Productivity", "Communication", "Teamwork", "Reliability", "Initiative",
      ];
      await supabase.from("hq_review_competencies").insert(defaults.map((name, i) => ({
        review_id: (data as Review).id, name, sort_order: i,
      })));
      setNewFor("");
      setEditing(data as Review);
      await load();
    }
  }

  const visible = filter === "all" ? rows : rows.filter(r => r.status === filter);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Performance Reviews</h1>

      <ReviewSchedule emps={emps} onChanged={load} />

      <div className="card flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="label">Start a new review</label>
          <select className="input" value={newFor} onChange={e => setNewFor(e.target.value)}>
            <option value="">— Select employee —</option>
            {emps.filter(e => e.id !== employee?.id).map(e => <option key={e.id} value={e.id}>{e.first_name ?? e.email} {e.last_name ?? ""}</option>)}
          </select>
        </div>
        <button onClick={create} disabled={!newFor} className="btn">+ Create draft</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["draft","sent_to_employee","employee_signed","closed","all"] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn btn-sm ${filter === s ? "" : "btn-ghost"}`}>
            {s.replace(/_/g, " ")} {s !== "all" && `(${rows.filter(r => r.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {visible.map(r => {
          const e = empById[r.employee_id];
          return (
            <button key={r.id} onClick={() => setEditing(r)} className="block w-full card text-left transition hover:ring-teal">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">{e ? (e.first_name ?? e.email) : "—"} · {r.cycle_name}</h2>
                  <div className="text-xs text-gray2">{fmtDay(r.period_start)} – {fmtDay(r.period_end)}</div>
                </div>
                <div className="flex gap-2">
                  {r.overall_rating && <span className="pill pill-mint">{r.overall_rating.replace(/_/g, " ")}</span>}
                  <span className={`pill ${statusPill(r.status)}`}>{r.status.replace(/_/g, " ")}</span>
                </div>
              </div>
            </button>
          );
        })}
        {visible.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No reviews.</div>}
      </div>

      {editing && <ReviewEditModal review={editing} emps={emps} onClose={() => setEditing(null)} onChanged={load} />}
    </div>
  );
}

type Cycle = "quarterly" | "semiannual" | "annual" | "none";
interface EmpWithReview { id: string; first_name: string | null; last_name: string | null; email: string; review_cycle?: Cycle; next_review_due_date?: string | null; }

function ReviewSchedule({ emps, onChanged }: { emps: Person[]; onChanged: () => Promise<void> }) {
  const [withReview, setWithReview] = useState<EmpWithReview[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { void load(); }, [emps.length]);
  async function load() {
    const { data } = await supabase.from("hq_employees_people")
      .select("id,first_name,last_name,email,review_cycle,next_review_due_date")
      .eq("status", "active")
      .order("next_review_due_date", { ascending: true, nullsFirst: false });
    setWithReview((data as EmpWithReview[]) ?? []);
  }

  function defaultDueDate(cycle: Cycle): string | null {
    if (cycle === "none") return null;
    const d = new Date();
    if (cycle === "quarterly") d.setMonth(d.getMonth() + 3);
    else if (cycle === "semiannual") d.setMonth(d.getMonth() + 6);
    else d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }

  async function updateCycle(empId: string, cycle: Cycle) {
    setBusy(empId);
    const due = defaultDueDate(cycle);
    await supabase.from("hq_employees_people")
      .update({ review_cycle: cycle, next_review_due_date: due })
      .eq("id", empId);
    setBusy(null);
    await load();
    await onChanged();
  }

  async function updateDue(empId: string, due: string) {
    setBusy(empId);
    await supabase.from("hq_employees_people")
      .update({ next_review_due_date: due || null })
      .eq("id", empId);
    setBusy(null);
    await load();
  }

  const today = new Date().toISOString().slice(0, 10);
  const dueSoon = withReview.filter(e => e.next_review_due_date && e.next_review_due_date <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) && e.review_cycle !== "none");

  return (
    <div className="card">
      <div className="mb-3">
        <h2 className="text-lg font-semibold">Review schedule</h2>
        <p className="text-xs text-gray2">Set a cadence per employee. {dueSoon.length > 0 && <span className="text-warn font-semibold">{dueSoon.length} due within 30 days.</span>}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2">
              <th className="py-2">Employee</th><th>Cycle</th><th>Next due</th><th></th>
            </tr>
          </thead>
          <tbody>
            {withReview.map(e => {
              const overdue = e.next_review_due_date && e.next_review_due_date < today;
              const soon = e.next_review_due_date && e.next_review_due_date <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
              return (
                <tr key={e.id} className="border-b border-line/70">
                  <td className="py-2">{[e.first_name, e.last_name].filter(Boolean).join(" ") || e.email}</td>
                  <td className="py-2">
                    <select className="input h-9" value={e.review_cycle ?? "annual"} disabled={busy === e.id} onChange={ev => void updateCycle(e.id, ev.target.value as Cycle)}>
                      <option value="quarterly">Quarterly</option>
                      <option value="semiannual">Semi-annual</option>
                      <option value="annual">Annual</option>
                      <option value="none">None</option>
                    </select>
                  </td>
                  <td className="py-2">
                    <input type="date" className={`input h-9 ${overdue ? "ring-bad" : soon ? "ring-warn" : ""}`} value={e.next_review_due_date ?? ""} disabled={busy === e.id || e.review_cycle === "none"} onChange={ev => void updateDue(e.id, ev.target.value)} />
                  </td>
                  <td className="py-2 text-xs">
                    {overdue ? <span className="text-bad font-semibold">overdue</span>
                      : soon ? <span className="text-warn font-semibold">soon</span>
                      : e.next_review_due_date ? <span className="text-gray2">scheduled</span>
                      : <span className="text-gray2">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Reviews() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Review[]>([]);
  const [open, setOpen] = useState<Review | null>(null);

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const { data } = await supabase.from("hq_performance_reviews").select("*")
      .eq("employee_id", employee.id).order("period_end", { ascending: false });
    setRows((data as Review[]) ?? []);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">My Performance Reviews</h1>
      {rows.length === 0 ? <div className="card py-12 text-center text-sm text-gray2">No reviews yet.</div> :
        rows.map(r => (
          <button key={r.id} onClick={() => setOpen(r)} className="block w-full card text-left transition hover:ring-teal">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold">{r.cycle_name ?? "Review"}</h2>
                <div className="text-xs text-gray2">{fmtDay(r.period_start)} – {fmtDay(r.period_end)}</div>
              </div>
              <div className="flex gap-2">
                {r.overall_rating && <span className="pill pill-mint">{r.overall_rating.replace(/_/g, " ")}</span>}
                <span className={`pill ${statusPill(r.status)}`}>{r.status.replace(/_/g, " ")}</span>
              </div>
            </div>
          </button>
        ))
      }
      {open && <EmployeeReviewModal review={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function ReviewEditModal({ review, emps, onClose, onChanged }: { review: Review; emps: Person[]; onClose: () => void; onChanged: () => Promise<void> }) {
  const [r, setR] = useState<Review>(review);
  const [comps, setComps] = useState<Competency[]>([]);
  const empById = Object.fromEntries(emps.map(e => [e.id, e]));

  useEffect(() => { void loadComps(); }, [review.id]);
  async function loadComps() {
    const { data } = await supabase.from("hq_review_competencies").select("*").eq("review_id", review.id).order("sort_order");
    setComps((data as Competency[]) ?? []);
  }

  async function patch(p: Partial<Review>) {
    setR({ ...r, ...p });
    await supabase.from("hq_performance_reviews").update(p).eq("id", r.id);
  }
  async function patchComp(c: Competency, p: Partial<Competency>) {
    setComps(prev => prev.map(x => x.id === c.id ? { ...x, ...p } : x));
    await supabase.from("hq_review_competencies").update(p).eq("id", c.id);
  }
  async function send() {
    await patch({ status: "sent_to_employee", sent_at: new Date().toISOString() });
    await onChanged(); onClose();
  }
  async function close() {
    await patch({ status: "closed", closed_at: new Date().toISOString() });
    await onChanged(); onClose();
  }

  const e = empById[r.employee_id];
  const editable = r.status === "draft";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={ev => ev.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold">{e ? (e.first_name ?? e.email) : "—"}</h2>
            <div className="text-sm text-gray2">{r.cycle_name} · {fmtDay(r.period_start)} – {fmtDay(r.period_end)}</div>
            <span className={`pill ${statusPill(r.status)} mt-1`}>{r.status.replace(/_/g, " ")}</span>
          </div>
          <CloseButton onClick={onClose} className="text-gray2 hover:text-ink" />
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Competencies</h3>
            <div className="space-y-2">
              {comps.map(c => (
                <div key={c.id} className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2 rounded-lg bg-offwhite p-3">
                  <div>
                    <div className="font-medium">{c.name}</div>
                    {editable
                      ? <div className="mt-1"><RichTextEditor value={c.comments ?? ""} onChange={html => patchComp(c, { comments: html || null })} minHeight={140} placeholder="Comments…" /></div>
                      : <AutoText text={c.comments} className="mt-1 text-sm" />}
                  </div>
                  <select disabled={!editable} className="input" value={c.rating ?? ""} onChange={e => patchComp(c, { rating: (e.target.value || null) as Rating | null })}>
                    <option value="">— Rate —</option>
                    {RATINGS.map(rt => <option key={rt} value={rt}>{rt.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Overall rating</label>
              <select disabled={!editable} className="input" value={r.overall_rating ?? ""} onChange={e => patch({ overall_rating: (e.target.value || null) as Rating | null })}>
                <option value="">—</option>
                {RATINGS.map(rt => <option key={rt} value={rt}>{rt.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>

          <div><label className="label">Strengths</label>{editable ? <RichTextEditor value={r.strengths ?? ""} onChange={html => patch({ strengths: html || null })} minHeight={140} placeholder="Strengths…" /> : <AutoText text={r.strengths} className="text-sm" />}</div>
          <div><label className="label">Growth areas</label>{editable ? <RichTextEditor value={r.growth_areas ?? ""} onChange={html => patch({ growth_areas: html || null })} minHeight={140} placeholder="Areas for improvement…" /> : <AutoText text={r.growth_areas} className="text-sm" />}</div>
          <div><label className="label">Goals for next period</label>{editable ? <RichTextEditor value={r.goals_for_next_period ?? ""} onChange={html => patch({ goals_for_next_period: html || null })} minHeight={140} placeholder="Goals for next period…" /> : <AutoText text={r.goals_for_next_period} className="text-sm" />}</div>
          <div><label className="label">Summary</label>{editable ? <RichTextEditor value={r.summary ?? ""} onChange={html => patch({ summary: html || null })} minHeight={140} placeholder="Summary…" /> : <AutoText text={r.summary} className="text-sm" />}</div>

          {r.employee_signed_at && (
            <div className="rounded-lg bg-ok-bg p-3 text-sm">
              <div className="font-semibold">✓ Employee signed {fmtDate(r.employee_signed_at)}</div>
              {r.employee_comments && <AutoText text={r.employee_comments} className="mt-1" />}
            </div>
          )}

          <div className="flex justify-end gap-2">
            {r.status === "draft" && <button onClick={send} className="btn">Send to employee →</button>}
            {r.status === "employee_signed" && <button onClick={close} className="btn">Close out review</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmployeeReviewModal({ review, onClose, onChanged }: { review: Review; onClose: () => void; onChanged: () => Promise<void> }) {
  const [r, setR] = useState<Review>(review);
  const [comps, setComps] = useState<Competency[]>([]);
  const [comments, setComments] = useState(review.employee_comments ?? "");
  useEffect(() => {
    void supabase.from("hq_review_competencies").select("*").eq("review_id", review.id).order("sort_order").then(({ data }) => setComps((data as Competency[]) ?? []));
  }, [review.id]);
  async function sign() {
    const { error } = await supabase.from("hq_performance_reviews").update({
      status: "employee_signed",
      employee_signed_at: new Date().toISOString(),
      employee_comments: comments || null,
    }).eq("id", r.id);
    if (!error) { await onChanged(); onClose(); }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
        <h2 className="mb-4 text-xl font-bold">{r.cycle_name}</h2>
        {r.overall_rating && <div className="mb-4"><span className="pill pill-mint text-base">Overall: {r.overall_rating.replace(/_/g, " ")}</span></div>}
        <div className="space-y-3">
          {comps.map(c => (
            <div key={c.id} className="rounded-lg bg-offwhite p-3">
              <div className="font-semibold">{c.name}{c.rating && <span className="ml-2 text-xs text-teal-dark">{c.rating.replace(/_/g, " ")}</span>}</div>
              {c.comments && <AutoText text={c.comments} className="mt-1 text-sm" />}
            </div>
          ))}
          {r.strengths && <Section label="Strengths" body={r.strengths} />}
          {r.growth_areas && <Section label="Growth areas" body={r.growth_areas} />}
          {r.goals_for_next_period && <Section label="Goals for next period" body={r.goals_for_next_period} />}
          {r.summary && <Section label="Summary" body={r.summary} />}

          {r.status === "sent_to_employee" && (
            <div className="rounded-lg bg-warn-bg p-4">
              <div className="font-semibold text-warn mb-2">⚠ Awaiting your acknowledgment</div>
              <label className="label">Your comments (optional)</label>
              <RichTextEditor value={comments} onChange={html => setComments(html)} minHeight={140} placeholder="Your comments…" />
              <button onClick={sign} className="btn mt-3">✓ I acknowledge this review</button>
            </div>
          )}
          {r.employee_signed_at && <div className="rounded-lg bg-ok-bg p-3 text-sm text-ok">✓ Signed {fmtDate(r.employee_signed_at)}</div>}
        </div>
      </div>
    </div>
  );
}

function Section({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray2">{label}</div>
      <AutoText text={body} className="mt-1 text-sm" />
    </div>
  );
}

function statusPill(s: Review["status"]): string {
  if (s === "closed") return "pill-ok";
  if (s === "employee_signed") return "pill-mint";
  if (s === "sent_to_employee") return "pill-warn";
  return "";
}
